import { Box } from '@mantine/core';
import { forwardRef } from 'react';
import styles from './HitSlop.module.css';

type HitSlopAmount = number | { top?: number; right?: number; bottom?: number; left?: number };

type HitSlopOwnProps = {
  amount?: HitSlopAmount;
  cursor?: React.CSSProperties['cursor'];
  zIndex?: number;
};

type HitSlopProps<C extends React.ElementType = 'div'> = HitSlopOwnProps &
  React.ComponentPropsWithoutRef<C> & {
    component?: C;
    children?: React.ReactNode;
  };

function HitSlopInner<C extends React.ElementType = 'div'>(
  { amount = 0, cursor, zIndex, component, children, style, className, ...props }: HitSlopProps<C>,
  ref: React.ForwardedRef<Element>,
) {
  const Component = (component || Box) as React.ElementType;

  const slop =
    typeof amount === 'number'
      ? { top: amount, right: amount, bottom: amount, left: amount }
      : { top: 0, right: 0, bottom: 0, left: 0, ...amount };

  const cssVars = {
    '--hitslop-top': slop.top,
    '--hitslop-right': slop.right,
    '--hitslop-bottom': slop.bottom,
    '--hitslop-left': slop.left,
    '--hitslop-cursor': cursor,
    '--hitslop-zindex': zIndex,
  } as React.CSSProperties;

  return (
    <Component
      ref={ref}
      className={`${styles.hitSlop}${className ? ` ${className}` : ''}`}
      style={{ ...cssVars, ...style }}
      {...props}
    >
      {children}
    </Component>
  );
}

export const HitSlop = forwardRef(HitSlopInner) as <C extends React.ElementType = 'div'>(
  props: HitSlopProps<C> & { ref?: React.ForwardedRef<Element> },
) => React.ReactElement | null;

(HitSlop as { displayName?: string }).displayName = 'HitSlop';
